// Roleta no estilo cassino: um tambor 3D com as sprites dos prêmios girando até parar no prêmio sorteado pelo servidor.
// CSS extra em client/css-parts/store.css (prefixo .rl-; o resto segue nas classes .cassino-* de styles.css).
// As sprites que passam são aleatórias (para ninguém decorar a sequência); só a que para no centro é o prêmio de verdade.
// Com 10 giros, a animação roda 10 vezes, uma de cada vez, sempre mostrando o prêmio daquele giro.
import { useEffect, useRef, useState } from 'preact/hooks';
import { PREY_CARD_ICON, ROULETTE, ROULETTE_PRICE, rouletteActive, type RoulettePrize } from '../../../../shared/src/game/roulette';

const PREY_IMG = '/assets/interface/images/prey-wildcards-icon.png';

type Result = { id: string; label: string; text: string; icon: number };
const CELL = 92;            // largura de cada casa do tambor
const STRIP = 64;           // casas na tira
const STOP = 54;            // casa onde o prêmio para
const SPIN_MS = 4600, SPIN10_MS = 2900, HOLD_MS = 1700;

/** brilho pela raridade do prêmio na roleta */
function tierOf(p: RoulettePrize | undefined): 'comum' | 'raro' | 'epico' | 'lendario' {
  const w = p?.w ?? 9999;
  return w <= 120 ? 'lendario' : w <= 250 ? 'epico' : w <= 450 ? 'raro' : 'comum';
}
function randomCell(): { icon: number; id: string } {
  const list = rouletteActive();
  const p = list[Math.floor(Math.random() * list.length)]!;
  const icon = p.items?.length ? p.items[Math.floor(Math.random() * p.items.length)]! : p.icon;
  return { icon, id: p.id };
}
const easeOut = (t: number) => 1 - Math.pow(1 - t, 4);

// resultado já animado: a janela pode ser remontada (re-render da tela) e não deve girar de novo
let lastPlayed: unknown = null;

export function Roulette(props: {
  tokens: number; result: Result[] | null; onSpin: (n: 1 | 10) => void; onStore: () => void;
  Img: (p: { typeId: number; count: number }) => preact.JSX.Element | null;
}) {
  const [cells, setCells] = useState(() => Array.from({ length: STRIP }, randomCell));
  const [x, setX] = useState(6 * CELL - (560 / 2 - CELL / 2));     // deslocamento da tira (px)
  const [spinning, setSpinning] = useState(false);
  const [shown, setShown] = useState<{ r: Result; n: number; of: number } | null>(null);
  const [tick, setTick] = useState(0);
  const raf = useRef(0), timers = useRef<number[]>([]);
  const queue = useRef<Result[]>([]), total = useRef(0);
  const winRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(560);
  const [help, setHelp] = useState(false);

  useEffect(() => {
    const el = winRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 560));
    ro.observe(el); return () => ro.disconnect();
  }, []);
  useEffect(() => () => { cancelAnimationFrame(raf.current); timers.current.forEach(clearTimeout); }, []);
  // tambor parado: centraliza a casa da vez na largura real da janela (no celular a janela é mais estreita)
  useEffect(() => { if (!spinning && !shown) setX(6 * CELL - (width / 2 - CELL / 2)); }, [width]);

  const spinOne = () => {
    const r = queue.current.shift();
    if (!r) { setSpinning(false); return; }
    const n = total.current - queue.current.length;
    const strip = Array.from({ length: STRIP }, (_, i) => (i === STOP ? { icon: r.icon, id: r.id } : randomCell()));
    setCells(strip); setShown(null); setSpinning(true);
    const center = width / 2 - CELL / 2;
    // para em um ponto aleatório dentro da casa (sem cair na borda) para não ficar sempre igual
    const jitter = (Math.random() - 0.5) * CELL * 0.55;
    const from = 6 * CELL - center, to = STOP * CELL - center + jitter;
    const dur = total.current > 1 ? SPIN10_MS : SPIN_MS;
    const t0 = performance.now();
    let lastCell = -1;
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / dur);
      const pos = from + (to - from) * easeOut(t);
      setX(pos);
      const c = Math.floor((pos + center + CELL / 2) / CELL);
      if (c !== lastCell) { lastCell = c; setTick((k) => k + 1); }
      if (t < 1) { raf.current = requestAnimationFrame(step); return; }
      // assenta no centro da casa
      setX(STOP * CELL - center);
      setShown({ r, n, of: total.current });
      timers.current.push(window.setTimeout(() => {
        if (queue.current.length) spinOne(); else setSpinning(false);
      }, queue.current.length ? HOLD_MS : 0));
    };
    raf.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    if (!props.result?.length || props.result === lastPlayed) return;
    lastPlayed = props.result;
    cancelAnimationFrame(raf.current); timers.current.forEach(clearTimeout); timers.current = [];
    queue.current = [...props.result]; total.current = props.result.length;
    spinOne();
  }, [props.result]);

  const center = width / 2;
  const first = Math.max(0, Math.floor((x - CELL) / CELL)), last = Math.min(STRIP - 1, Math.ceil((x + width + CELL) / CELL));
  const prize = shown ? ROULETTE.find((p) => p.id === shown.r.id) : undefined;
  const tier = tierOf(prize);
  const active = rouletteActive(), sum = active.reduce((t, p) => t + p.w, 0);

  return (
    <div class="cassino rl">
      <div class="cassino-marquee">{Array.from({ length: 22 }, (_, i) => <i key={i} style={{ animationDelay: `${(i % 4) * 0.18}s` }} />)}</div>
      <button class={'rl-help' + (help ? ' on' : '')} onClick={() => setHelp(!help)} title="Como funciona">?</button>
      <div class="cassino-title"><span>★</span> ROLETA DE NOXARA <span>★</span></div>
      <div class="rl-sub">Gire o tambor e leve o prêmio onde a seta parar</div>
      {help && (
        <div class="rl-steps">
          <div><i>🎟️</i><b>1. Fichas</b><small>Cada giro gasta 1 Ficha da Roleta. Compre na Loja ({ROULETTE_PRICE} coins cada) ou ganhe no passe de batalha.</small></div>
          <div><i>🎰</i><b>2. Gire</b><small>Use GIRAR para um giro ou 10 GIROS para dez seguidos, um de cada vez.</small></div>
          <div><i>📦</i><b>3. Prêmio</b><small>O prêmio vai para a Noxara Store Box. Veja as chances em "Prêmios e chances".</small></div>
        </div>
      )}
      <div class={'cassino-drum' + (shown && !queue.current.length ? '' : '') + (shown ? ` win-${tier}` : '')}>
        <div class="cassino-win" ref={winRef}>
          {cells.slice(first, last + 1).map((c, k) => {
            const i = first + k;
            const left = i * CELL - x;
            const d = (left + CELL / 2 - center) / (width / 2);       // -1 (esquerda) .. 1 (direita)
            const ad = Math.min(1, Math.abs(d));
            const p = ROULETTE.find((q) => q.id === c.id);
            const hit = shown && i === STOP;
            return (
              <div key={i} class={'cassino-cell t-' + tierOf(p) + (hit ? ' hit' : '')}
                style={{ left: `${left}px`, transform: `perspective(520px) rotateY(${(-d * 62).toFixed(1)}deg) scale(${(1 - ad * 0.28).toFixed(3)})`,
                  opacity: (1 - ad * 0.65).toFixed(3), zIndex: 100 - Math.round(ad * 50) }}>
                <div class="cassino-sprite">{c.icon === PREY_CARD_ICON ? <img class="cassino-card" src={PREY_IMG} alt="" /> : <props.Img typeId={c.icon} count={c.icon === 22118 ? 50 : 1} />}</div>
              </div>
            );
          })}
          <i class="cassino-shade l" /><i class="cassino-shade r" />
          <i class="cassino-line" />
        </div>
        <i key={tick} class={'cassino-pointer top' + (spinning && !shown ? ' tick' : '')} />
        <i class="cassino-pointer bottom" />
      </div>
      <div class={'cassino-result' + (shown ? ` show r-${tier}` : '')}>
        {shown ? (
          <>
            <span class="cassino-res-sprite">{shown.r.icon === PREY_CARD_ICON ? <img class="cassino-card" src={PREY_IMG} alt="" /> : <props.Img typeId={shown.r.icon} count={shown.r.icon === 22118 ? 50 : 1} />}</span>
            <span>
              {shown.of > 1 && <small>Giro {shown.n} de {shown.of}</small>}
              <b>{shown.r.label}</b>
              <em>{shown.r.text}</em>
            </span>
          </>
        ) : spinning ? <em class="cassino-rolling">Girando...</em> : <em>Boa sorte! Cada giro gasta 1 Ficha da Roleta da sua Noxara Store Box.</em>}
      </div>
      <div class="cassino-controls">
        <button class="cassino-btn" disabled={spinning || props.tokens < 1} onClick={() => props.onSpin(1)}>GIRAR<small>1 ficha</small></button>
        <button class="cassino-btn big" disabled={spinning || props.tokens < 10} onClick={() => props.onSpin(10)}>10 GIROS<small>10 fichas</small></button>
        <div class={'cassino-tokens' + (props.tokens < 1 ? ' rl-empty' : '')} title={props.tokens < 1 ? 'Sem fichas: compre na Loja' : ''}><props.Img typeId={19082} count={1} /><span>Fichas<b>{props.tokens}</b></span></div>
        <button class="cassino-btn buy" onClick={props.onStore}>Comprar fichas<small>{ROULETTE_PRICE} coins cada</small></button>
      </div>
      <details class="cassino-odds"><summary>Prêmios e chances</summary>
        <div class="cassino-odds-list">
          {active.map((p) => (
            <span key={p.id} class={'t-' + tierOf(p)}><i>{p.icon === PREY_CARD_ICON ? <img class="cassino-card" src={PREY_IMG} alt="" /> : <props.Img typeId={p.icon} count={p.icon === 22118 ? 50 : 1} />}</i>{p.label}<b>{(p.w / sum * 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%</b></span>
          ))}
        </div>
        <p class="small muted">Os equipamentos sorteados ainda ganham raridade: 89% comum, 8,5% raro, 2% épico e 0,5% lendário.
          Prêmios presos ao personagem, entregues na Noxara Store Box. As bags abrem com "Usar". Jogo para maiores de 18 anos.</p>
      </details>
    </div>
  );
}
